import { ROUTES } from "./routes";
import { splitInstancePath } from "./instancePath";

/**
 * Where each screen's help is (round 7): the page of the user guide, and the section on it, that
 * **Help for this page** in the page header opens (`components/shell/PageHeader.tsx`). The web
 * app serves the guide at `/help` (`server/help.ts`), built from `docs/guide`.
 *
 * ONE table, keyed by route path. Every path in `ROUTES` and `APP_ROUTES` has a row
 * (`lib/__tests__/helpLinks.test.ts`); `null` says a screen has no help, on purpose. A deeper
 * location takes the longest row that is a prefix of it, and its `detail` section when the row
 * has one: `/runs/<id>` is the Runs page's "A run". `page` and the sections are the guide's own
 * names (`scripts/guide/plan.ts` slugs, heading slugs); `guideConsistency.test.ts` checks each
 * one exists, so a renamed heading fails a test instead of opening the top of the page.
 */

export interface HelpTarget {
  /** The guide page's slug: `connections` for `docs/guide/connections.md`. */
  page: string;
  /** The heading's slug on that page (`add-a-connection`); absent opens the top of the page. */
  section?: string;
  /** The section for a location under this path that has no row of its own (a detail screen). */
  detail?: string;
}

export const HELP_BASE = "/help";

export const HELP_LINKS: Readonly<Record<string, HelpTarget | null>> = {
  [ROUTES.overview]: { page: "overview" },
  [ROUTES.connections]: { page: "connections", detail: "change-a-connection" },
  [`${ROUTES.connections}/new`]: { page: "connections", section: "add-a-connection" },
  [ROUTES.schedules]: { page: "schedules", detail: "add-or-change-a-schedule" },
  [`${ROUTES.schedules}/new`]: { page: "schedules", section: "add-or-change-a-schedule" },
  [ROUTES.runs]: { page: "runs", detail: "a-run" },
  [ROUTES.transformations]: { page: "transformations" },
  [ROUTES.yourData]: { page: "your-data" },
  [ROUTES.monitor]: { page: "skymonitor" },
  [ROUTES.settingsDestination]: { page: "settings", section: "destination" },
  [ROUTES.settings]: { page: "settings", section: "general" },
  [ROUTES.settingsNotifications]: { page: "settings", section: "notifications" },
  [ROUTES.settingsConnectors]: { page: "settings", section: "connectors" },
  [ROUTES.adminUsers]: { page: "settings", section: "users" },
  [ROUTES.adminAudit]: { page: "audit" },
  [ROUTES.adminInstances]: { page: "platform-admin", section: "instances" },
  [ROUTES.adminConnectors]: { page: "platform-admin", section: "connectors" },
  [ROUTES.adminTenants]: { page: "platform-admin", section: "tenants" },
  // Merged into Tenants (round 6 T-04); the address only redirects there.
  [ROUTES.adminCustomers]: { page: "platform-admin", section: "tenants" },
  [ROUTES.adminPlatformSettings]: { page: "platform-admin", section: "platform-settings" },
  // AI apps over MCP (round 8, ADR 0009): one guide page, `docs/guide/ai-apps.md` (pack A4).
  [ROUTES.connectedApps]: { page: "ai-apps", section: "see-and-end-your-connections" },
  [ROUTES.adminAiApps]: { page: "ai-apps", section: "see-and-end-your-connections" },
  // Release 8B: the approve link of a change an AI app asked to make.
  [ROUTES.mcpApprove]: { page: "ai-apps", section: "approve-a-change" },
  // #1271: a person's own watches, the page a watcher's email links.
  [ROUTES.myAlerts]: { page: "connections", section: "watch-a-connection" },
};

/**
 * Locations a path prefix cannot name, checked before `HELP_LINKS`: a record's own sub-screen.
 * `guideConsistency.test.ts` checks these targets as it checks the rows above.
 */
export const HELP_PATTERN_LINKS: ReadonlyArray<{ pattern: RegExp; target: HelpTarget }> = [
  // ADR 0010: a tenant's own link (`/admin/tenants/<id>`) opens its dialog and, in a per-tenant
  // deployment, its resources.
  {
    pattern: /^\/admin\/tenants\/[^/]+$/,
    target: { page: "platform-admin", section: "what-a-tenant-gets" },
  },
  // Release 8B (ADR 0009 section 7): a draft connection an AI app created, finished here.
  {
    pattern: /^\/connections\/[^/]+\/setup$/,
    target: { page: "ai-apps", section: "finish-a-connection-an-ai-app-created" },
  },
];

/**
 * Level B reliability (#828): the screens that show a success rate, a last load or a Late badge
 * link to the reliability page's section that explains them ("What these numbers mean"), one row
 * per screen. Each screen keeps its own **Help for this page** row above; these sit beside the
 * numbers. `guideConsistency.test.ts` checks these targets as it checks the rows above.
 */
export const RELIABILITY_HELP = {
  connections: { page: "reliability", section: "last-loaded-and-late" },
  schedules: { page: "reliability", section: "success-rate" },
  overview: { page: "reliability", section: "what-the-numbers-count" },
  yourData: { page: "reliability", section: "last-loaded-and-late" },
  runDetail: { page: "reliability", section: "rows-per-load" },
} as const satisfies Readonly<Record<string, HelpTarget>>;

/** The guide address for a target: `/help/runs#runs--a-run`, as the guide's anchors are scoped. */
export function helpHref(page: string, section?: string): string {
  return section ? `${HELP_BASE}/${page}#${page}--${section}` : `${HELP_BASE}/${page}`;
}

/**
 * The help address for a location, or null when the screen has none. The rows are keyed by the
 * screen's own path; an instance's prefix (`/i/<id>`, round 9 T-58) is not part of it.
 */
export function helpHrefFor(location: string): string | null {
  const pathname = splitInstancePath(location.split(/[?#]/)[0] ?? "").inner;
  const patterned = HELP_PATTERN_LINKS.find(({ pattern }) => pattern.test(pathname));
  if (patterned) return helpHref(patterned.target.page, patterned.target.section);
  if (pathname in HELP_LINKS) {
    const target = HELP_LINKS[pathname];
    return target ? helpHref(target.page, target.section) : null;
  }
  const prefix = Object.keys(HELP_LINKS)
    .filter((path) => pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
  const target = prefix === undefined ? null : HELP_LINKS[prefix];
  if (!target) return null;
  return helpHref(target.page, target.detail ?? target.section);
}
